import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Pencil, Plus } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";

const STATUS_STYLE: Record<string, string> = {
  open: "border-[#00E5FF]/50 text-[#00E5FF]",
  resolved: "border-[#22C55E]/50 text-[#22C55E]",
  void: "border-white/20 text-gray-400",
};

export default function AdminPredictions() {
  const qc = useQueryClient();
  const [newSlug, setNewSlug] = useState("");
  const [newQ, setNewQ] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const [editQ, setEditQ] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["admin-predictions"],
    queryFn: async () => (await api.get("/admin/predictions")).data,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-predictions"] });

  const resolve = useMutation({
    mutationFn: async ({ id, outcome }: { id: string; outcome: string }) =>
      (await api.patch(`/admin/predictions/${id}`, { outcome })).data,
    onSuccess: (d: any) => {
      toast.success(d.correct_calls != null
        ? `${d.message} — ${d.correct_calls} correct call${d.correct_calls === 1 ? "" : "s"} scored`
        : d.message);
      refresh();
    },
    onError: (e: any) => toast.error(e.response?.data?.detail || "Failed to resolve"),
  });

  const create = async () => {
    try {
      await api.post("/admin/predictions", { slug: newSlug.trim(), question: newQ.trim() });
      toast.success("Poll created — it's live in the article rail");
      setNewSlug(""); setNewQ("");
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveQuestion = async (id: string) => {
    try {
      await api.patch(`/admin/predictions/${id}/question`, { question: editQ.trim() });
      toast.success("Question updated");
      setEditing(null);
      refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  const rows: any[] = data || [];

  return (
    <div data-testid="admin-predictions-page">
      <h1 className="font-heading font-bold text-white text-2xl">Reader Polls</h1>
      <p className="text-gray-500 text-sm mt-1 mb-5">
        Polls run in the article right rail. Resolve them once the outcome is known — correct voters earn a spot on the Top Callers leaderboard.
      </p>

      <div className="bg-[#121212] border border-white/10 rounded-lg p-4 mb-6" data-testid="poll-create-form">
        <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-3">New poll</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Input value={newSlug} onChange={(e) => setNewSlug(e.target.value)} placeholder="article-slug"
            data-testid="poll-create-slug" className="bg-black/40 border-white/10 text-white sm:max-w-[280px]" />
          <Input value={newQ} onChange={(e) => setNewQ(e.target.value)}
            placeholder="Yes/No question readers will vote on (10-200 chars)"
            data-testid="poll-create-question" className="bg-black/40 border-white/10 text-white flex-1" />
          <Button onClick={create} disabled={!newSlug.trim() || newQ.trim().length < 10}
            data-testid="poll-create-submit"
            className="bg-[#00E5FF] text-black font-bold hover:bg-[#00c9e0] shrink-0">
            <Plus className="h-4 w-4 mr-1" /> Create
          </Button>
        </div>
      </div>

      {isLoading ? (
        <p className="text-gray-500 text-sm">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-gray-500 text-sm">No polls yet — they're generated with new articles.</p>
      ) : (
        <div className="space-y-3">
          {rows.map((p) => (
            <div key={p.id} data-testid={`admin-poll-${p.id}`}
              className="bg-[#121212] border border-white/10 rounded-lg p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  {editing === p.id ? (
                    <div className="flex gap-2">
                      <Input value={editQ} onChange={(e) => setEditQ(e.target.value)}
                        data-testid={`poll-edit-input-${p.id}`}
                        className="bg-black/40 border-white/10 text-white text-sm" />
                      <Button size="sm" onClick={() => saveQuestion(p.id)} disabled={editQ.trim().length < 10}
                        data-testid={`poll-edit-save-${p.id}`}
                        className="bg-[#00E5FF] text-black font-bold hover:bg-[#00c9e0] h-9 text-xs shrink-0">Save</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditing(null)}
                        className="border-white/15 text-gray-400 hover:bg-white/5 h-9 text-xs shrink-0">Cancel</Button>
                    </div>
                  ) : (
                    <p className="text-white text-sm font-semibold leading-snug flex items-start gap-2">
                      <span>{p.question}</span>
                      {p.status === "open" && (
                        <button onClick={() => { setEditing(p.id); setEditQ(p.question); }}
                          data-testid={`poll-edit-${p.id}`} title="Edit question"
                          className="text-gray-600 hover:text-[#00E5FF] transition-colors shrink-0 mt-0.5">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </p>
                  )}
                  <p className="font-mono text-[11px] text-gray-500 mt-1">
                    <Link to={`/article/${p.slug}`} target="_blank" className="hover:text-[#00E5FF]">{p.slug}</Link>
                    {" · "}Yes {p.yes ?? 0} / No {p.no ?? 0}
                    {p.outcome ? ` · outcome: ${p.outcome.toUpperCase()}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge variant="outline"
                    className={`${STATUS_STYLE[p.status] || "border-white/20 text-gray-300"} font-mono text-[10px] uppercase`}>
                    {p.status}
                  </Badge>
                  {p.status === "open" && (
                    <>
                      <Button size="sm" disabled={resolve.isPending}
                        data-testid={`poll-resolve-yes-${p.id}`}
                        onClick={() => resolve.mutate({ id: p.id, outcome: "yes" })}
                        className="bg-[#22C55E] text-black font-bold hover:bg-[#16a34a] h-7 text-xs">
                        Yes happened
                      </Button>
                      <Button size="sm" disabled={resolve.isPending}
                        data-testid={`poll-resolve-no-${p.id}`}
                        onClick={() => resolve.mutate({ id: p.id, outcome: "no" })}
                        className="bg-[#DC2626] text-white font-bold hover:bg-[#b91c1c] h-7 text-xs">
                        No, it didn't
                      </Button>
                      <Button size="sm" variant="outline" disabled={resolve.isPending}
                        data-testid={`poll-resolve-void-${p.id}`}
                        onClick={() => resolve.mutate({ id: p.id, outcome: "void" })}
                        className="border-white/15 text-gray-400 hover:bg-white/5 h-7 text-xs">
                        Void
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
